import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardImg, CardTitle, Col, Container, Input, Nav, NavItem, Row, TabContent, TabPane } from 'reactstrap';
import { Search } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import classnames from "classnames";
import { getdevelopers } from 'service/api';
import Footer from 'components/VerticalLayout/Footer';

const Builders = () => {
    const [customActiveTab, setCustomActiveTab] = useState("1");
    const [searchQuery, setSearchQuery] = useState("");
    const [developersData, setDevelopersData] = useState([]);
    const [locations, setLocations] = useState([]);

    const toggleCustom = tab => {
        if (customActiveTab !== tab) {
            setCustomActiveTab(tab);
        }
    };

    useEffect(() => {
        getDeveloper();
    }, []);

    const getDeveloper = async () => {
        let response = await getdevelopers();
        setDevelopersData(response.data);

        // Extract unique locations from all developers
        const allLocations = new Set();
        response.data.forEach(dev => {
            dev.ongoing_projects.forEach(project => {
                if (project.location) {
                    allLocations.add(project.location);
                }
            });
            dev.completed_projects.forEach(project => {
                if (project.location) {
                    allLocations.add(project.location);
                }
            });
        });

        // Convert Set to array and create location objects with IDs
        const locationsArray = Array.from(allLocations).map((location, index) => ({
            id: (index + 1).toString(),
            name: location
        }));

        setLocations(locationsArray);

        // Set first location as active tab if locations exist
        if (locationsArray.length > 0) {
            setCustomActiveTab(locationsArray[0].id);
        }
    }

    // Organize developers by location
    const getDevelopersByLocation = () => {
        const developersByLocation = {};

        locations.forEach(location => {
            const devsInLocation = developersData.filter(dev => {
                // Check if developer has projects in this location
                return (
                    dev.ongoing_projects.some(project => project.location === location.name) ||
                    dev.completed_projects.some(project => project.location === location.name)
                );
            });

            developersByLocation[location.id] = {
                name: location.name,
                developers: devsInLocation.map(dev => ({
                    id: dev.id,
                    name: dev.dev_name,
                    image: dev.logo,
                    operatingSince: dev.operating_since,
                    projects: `${dev.total_projects} Projects in ${location.name}`,
                    completed: `${dev.ready_projects} Completed`,
                    tagline: dev.about || "Experienced real estate developer"
                }))
            };
        });

        return developersByLocation;
    };

    // Filter developers based on search query
    const filterDevelopers = (developers) => {
        if (!searchQuery.trim()) return developers;

        const query = searchQuery.toLowerCase();
        return developers.filter(dev =>
            dev.name.toLowerCase().includes(query) ||
            (dev.tagline && dev.tagline.toLowerCase().includes(query))
        );
    };

    // Render developer card with fixed dimensions
    const renderDeveloperCard = (developer) => (
        <Col md={6} lg={4} xl={3} key={developer.id} className="mb-4">
            <Card className="text-light h-100" style={{
                backgroundColor: '#1E253A',
                borderRadius: '10px',
                border: 'none',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px'
                }}>
                    {developer.image ? (
                        <CardImg
                            top
                            src={developer.image}
                            alt={developer.name}
                            style={{
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <div style={{
                            color: '#1E253A',
                            fontSize: '1rem',
                            textAlign: 'center',
                            padding: '20px'
                        }}>
                            Image not available
                        </div>
                    )}
                </div>

                <CardBody className="d-flex flex-column">
                    <CardTitle className="mt-0" style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                        {developer.name}
                    </CardTitle>

                    <div style={{ marginBottom: '15px' }}>
                        <div style={{
                            position: "relative",
                            paddingLeft: "20px",
                            marginBottom: '8px'
                        }}>
                            <span style={{
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: "gold"
                            }}></span>
                            Operating Since {developer.operatingSince}
                        </div>

                        <div style={{ position: "relative", paddingLeft: "16px", marginBottom: '4px' }}>
                            <span style={{
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: "gold"
                            }}></span>
                            {developer.projects}
                        </div>
                        <div style={{ position: "relative", paddingLeft: "16px" }}>
                            <span style={{
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: "gold"
                            }}></span>
                            {developer.completed}
                        </div>
                    </div>

                    <div className="mt-auto">
                        <Link
                            to={`Details/${developer.name ?
                                developer.name.toLowerCase().replace(/\s+/g, '-') :
                                developer.id}`}
                            className="btn btn-warning w-100"
                            style={{
                                borderRadius: '20px',
                                fontWeight: '500',
                                border: 'none'
                            }}
                        >
                            See Details
                        </Link>
                    </div>

                    {/* <div className="mt-auto">
                        <Link
                            to={`Details/${developer.dev_name}`}
                            className="btn btn-warning w-100"
                            style={{
                                borderRadius: '20px',
                                fontWeight: '500',
                                border: 'none'
                            }}
                        >
                            See Details
                        </Link>
                    </div> */}
                </CardBody>
            </Card>
        </Col>
    );

    const developersByLocation = getDevelopersByLocation();

    return (
        <React.Fragment>
            <div className="page-content" style={{
                color: "#fff",
                fontFamily: "'Roboto', sans-serif",
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                minHeight: '100vh'
            }}>
                <Container fluid className="py-4">
                    <Row className="justify-content-center">
                        <Col xl={10} lg={12} md={12}>
                            <Card style={{
                                background: "rgba(255, 255, 255, 0.05)",
                                border: "1px solid rgba(255, 215, 0, 0.1)",
                                borderRadius: "15px",
                                padding: "20px",
                                backdropFilter: 'blur(10px)'
                            }}>
                                <CardBody className="d-flex flex-column align-items-center">
                                    <CardTitle className="mb-4 text-center" style={{
                                        fontSize: "1.8rem",
                                        fontWeight: "600",
                                        color: 'gold'
                                    }}>
                                        Developers in Vadodara
                                    </CardTitle>

                                    {/* Search Bar */}
                                    <div className="w-100 mb-4" style={{ position: "relative", maxWidth: '600px' }}>
                                        <Input
                                            type="text"
                                            placeholder="Search by Developer Name or Tagline..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{
                                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                border: "1px solid rgba(255, 215, 0, 0.4)",
                                                color: "#fff",
                                                padding: "12px 50px 12px 20px",
                                                borderRadius: "30px",
                                                fontSize: "1rem",
                                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                                width: "100%",
                                                height: '50px'
                                            }}
                                        />
                                        <Search
                                            color="gold"
                                            size={20}
                                            style={{
                                                position: "absolute",
                                                right: "20px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                pointerEvents: "none",
                                            }}
                                        />
                                    </div>

                                    {/* Location Tabs */}
                                    {locations.length > 0 && (
                                        <Nav
                                            tabs
                                            className="nav-tabs-custom w-100 justify-content-center flex-wrap gap-3"
                                        >
                                            {locations.map(location => (
                                                <NavItem key={location.id} className="text-center">
                                                    <NavLink
                                                        style={{
                                                            cursor: "pointer",
                                                            borderBottom: customActiveTab === location.id ? "2px solid gold" : "none",
                                                            transition: "all 0.3s ease",
                                                            fontWeight: "500",
                                                            color: customActiveTab === location.id ? "gold" : "#fff",
                                                            padding: "8px 12px",
                                                            margin: "4px",
                                                            borderRadius: '6px',
                                                            whiteSpace: "nowrap",
                                                            backgroundColor: customActiveTab === location.id
                                                                ? 'rgba(255, 215, 0, 0.1)'
                                                                : 'transparent',
                                                        }}
                                                        className={classnames({
                                                            active: customActiveTab === location.id,
                                                        })}
                                                        onClick={() => toggleCustom(location.id)}
                                                    >
                                                        {location.name}
                                                    </NavLink>
                                                </NavItem>
                                            ))}
                                        </Nav>
                                    )}

                                    {/* Location Title */}
                                    {developersByLocation[customActiveTab] && (
                                        <div className="w-100 mt-4 mb-3">
                                            <h3 style={{
                                                color: 'gold',
                                                borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
                                                paddingBottom: '10px'
                                            }}>
                                                Properties in {developersByLocation[customActiveTab].name}
                                            </h3>
                                        </div>
                                    )}

                                    <TabContent activeTab={customActiveTab} className="w-100">
                                        {Object.entries(developersByLocation).map(([tabId, tabData]) => (
                                            <TabPane key={tabId} tabId={tabId}>
                                                <Row className="justify-content-start">
                                                    {filterDevelopers(tabData.developers).length > 0 ? (
                                                        filterDevelopers(tabData.developers).map(renderDeveloperCard)
                                                    ) : (
                                                        <Col xs={12} className="text-center py-5">
                                                            <h4 style={{ color: 'rgba(255,255,255,0.7)' }}>
                                                                No developers found matching your search in {tabData.name}
                                                            </h4>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </TabPane>
                                        ))}
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Footer */}
            <Footer />

        </React.Fragment >
    );
}

export default Builders;